// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

// D-COST-6 v2.3 / v2.4 — where the numbers come from and whether each feed is healthy, plus month close.
// Feeds run daily at 06:20 UTC; "Refresh now" runs one immediately (audited). Months freeze automatically on
// day N of the next month; an admin can close a past month early or reopen a closed one (reason required).

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Box from '@mui/material/Box'
import { RefreshCw } from 'lucide-react'
import AppButton from '../../../components/ui/AppButton'
import ConfirmDialog from '../../../components/ui/ConfirmDialog'
import LoadState from '../../../components/ui/LoadState'
import Section from '../../../components/ui/Section'
import StatusChip from '../../../components/ui/StatusChip'
import { useToast } from '../../../components/ui/Toast'
import { DataGrid, type DataGridColumn } from '../../../components/DataGrid'
import { friendlyError } from '../../../api/errors'
import type { StatusTone } from '../../../api/status'
import {
  closeFinanceMonth, financeKeys, getFinanceSources, refreshFinanceSource, reopenFinanceMonth,
  type FinanceFeed, type FinanceFeedSource, type FinanceMonthRow,
} from '../../../api/finance'
import { STATUS, money } from './FinanceTables'

const when = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString() : 'never')
const RUN_TONE: Record<string, StatusTone> = { succeeded: 'green', skipped: 'grey', failed: 'red', running: 'blue' }

function FeedCard({ feed, onRefresh, busy }: { feed: FinanceFeed; onRefresh: () => void; busy: boolean }) {
  const r = feed.last_run
  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 1.5, display: 'grid', gap: 0.75 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ fontWeight: 700, fontSize: 13.5 }}>{feed.label}</Box>
        {!feed.configured
          ? <StatusChip status="off" tone="amber" label="not configured" />
          : r ? <StatusChip status={r.status} tone={RUN_TONE[r.status] ?? 'grey'} label={`last run ${r.status}`} />
          : <StatusChip status="never" tone="grey" label="never run" />}
        <Box sx={{ flex: 1 }} />
        <AppButton size="small" startIcon={<RefreshCw size={13} />} disabled={busy} onClick={onRefresh}>Refresh now</AppButton>
      </Box>
      <Box sx={{ fontSize: 12.5, color: 'text.secondary' }}>
        Last good data: <b>{when(feed.last_success_at)}</b>
        {r && <> · last run {when(r.finished_at ?? r.started_at)} by {r.triggered_by}{r.total_usd !== null ? ` · ${money(r.total_usd)}` : ''}</>}
      </Box>
      {r?.detail && <Box sx={{ fontSize: 12.5, color: r.status === 'failed' ? 'error.main' : 'text.secondary' }}>{r.detail}</Box>}
      {(!feed.configured || r?.status === 'failed' || r?.status === 'skipped') && (
        <Box sx={{ fontSize: 12, color: 'text.secondary' }}>How to connect: {feed.how_to}</Box>
      )}
    </Box>
  )
}

export default function DataSourcesPanel({ history, currentMonth, autoCloseDay, autoCloseEnabled }: {
  history: FinanceMonthRow[]; currentMonth: string; autoCloseDay: number; autoCloseEnabled: boolean
}) {
  const qc = useQueryClient()
  const toast = useToast()
  const q = useQuery({ queryKey: financeKeys.sources, queryFn: getFinanceSources })
  const [reopen, setReopen] = useState<string | null>(null)

  const run = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: financeKeys.all }) },
  })
  const act = (fn: () => Promise<unknown>, done: string, after?: () => void) => {
    run.reset()
    run.mutate(fn, { onSuccess: () => { toast.show(done, 'success'); after?.() }, onError: e => { if (!after) toast.show(friendlyError(e), 'error') } })
  }
  const refresh = (source: FinanceFeedSource) =>
    act(() => refreshFinanceSource(source).then(r => { if (r.status === 'failed') throw new Error(r.detail ?? 'refresh failed') }), 'Feed refreshed')

  const monthCols: DataGridColumn<FinanceMonthRow>[] = [
    { key: 'month', header: 'Month', value: r => r.month, render: r => <b>{r.month}</b> },
    { key: 'status', header: 'State', value: r => r.status, render: r => <Box sx={{ color: STATUS[r.status].color }}>{STATUS[r.status].label}</Box> },
    { key: 'source', header: 'Azure figures from', value: r => r.cost_source,
      render: r => r.cost_source === 'azure_export' ? 'cost export' : 'hourly snapshot (may read low)' },
    { key: 'net', header: 'Net profit', align: 'right', value: r => r.net_profit_usd, render: r => money(r.net_profit_usd) },
    {
      key: 'act', header: '', noFilter: true, width: 140,
      render: r => r.status === 'closed'
        ? <AppButton size="small" onClick={() => { run.reset(); setReopen(r.month) }}>Reopen…</AppButton>
        : r.month < currentMonth
          ? <AppButton size="small" disabled={run.isPending} onClick={() => act(() => closeFinanceMonth(r.month), `${r.month} closed`)}>Close now</AppButton>
          : null,
    },
  ]

  return (
    <>
      <Section title="Data sources" subtitle="Feeds run every day at 06:20 UTC. “Refresh now” runs one immediately; every run is recorded.">
        <LoadState query={q} what="the data sources" isEmpty={() => false} empty={null}>
          {data => (
            <Box sx={{ display: 'grid', gap: 1.25 }}>
              {data.feeds.map(f => <FeedCard key={f.source} feed={f} busy={run.isPending} onRefresh={() => refresh(f.source)} />)}
              <Box sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                {data.snapshot.label}: last captured <b>{when(data.snapshot.last_captured_at)}</b>. Used for any month the cost export does not cover yet.
              </Box>
            </Box>
          )}
        </LoadState>
      </Section>
      <Section title="Month close"
        subtitle={autoCloseEnabled
          ? `Months close automatically on day ${autoCloseDay} of the next month, once Azure’s late charges have landed. A closed month is frozen and never changes unless reopened; a reopened month re-closes automatically after 24 hours.`
          : 'Automatic close is PAUSED (Settings) — nothing closes on its own while you back-fill past months. Close a month by hand here when its numbers are complete.'}>
        {autoCloseEnabled && (
          <Box sx={{ mb: 1 }}>
            <AppButton size="small" disabled={run.isPending} onClick={() => act(() => refreshFinanceSource('close'), 'Month close checked')}>Run automatic close now</AppButton>
          </Box>
        )}
        <DataGrid rows={history} columns={monthCols} storageKey="finance-v2-month-close" getRowKey={r => r.month} dense />
      </Section>
      <ConfirmDialog open={reopen !== null} title={`Reopen ${reopen ?? ''}?`}
        body="The month’s frozen figures stay on record. While reopened it shows live figures; it re-closes automatically after 24 hours as a new version."
        requireReason={{ label: 'Why reopen it?', minLength: 5, placeholder: 'e.g. late invoice from a vendor' }}
        confirmLabel="Reopen" busy={run.isPending} error={run.isError ? friendlyError(run.error) : null}
        onCancel={() => { if (!run.isPending) setReopen(null) }}
        onConfirm={({ reason }) => { const m = reopen; if (m) act(() => reopenFinanceMonth(m, reason), `${m} reopened`, () => setReopen(null)) }} />
    </>
  )
}
